import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useGetMaterialsQuery } from '@/app/services/materials'
import { useGetRatesQuery } from '@/app/services/rates'
import { useGetRateTablesQuery } from '@/app/services/rateTables'
import { useGetSheetsQuery } from '@/app/services/sheets'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { isThicknessWithinTolerance } from '@/common/utils/SheetUtils/SheetUtils'

import useNumberFormatter from './useNumberFormatter'
import { useToolBoxTreatments } from './useToolBoxTreatments'

const compareMaterials = (a, b) => {
    if (a.materialName.toLocaleLowerCase() < b.materialName.toLocaleLowerCase()) {
        return -1
    }
    if (a.materialName.toLocaleLowerCase() > b.materialName.toLocaleLowerCase()) {
        return 1
    }
    return 0
}

export function useQuoteItemPropertyOptions({
    cuttingTechnologyId: cuttingTechnologyValue,
    materialId: materialValue,
    mode,
    partDimensions,
    profileDimensionsValue = null,
    profileValue = null,
    thickness: thicknessValue,
}) {
    const { t } = useTranslation()

    const { showTubeOptions } = useToolBoxTreatments()

    const organisationId = useSelector(selectOrganisationId)
    const organisation = useSelector(selectOrganisation)
    const { data: materials } = useGetMaterialsQuery({ organisationId })
    const { data: sheets } = useGetSheetsQuery({ organisationId })
    const { data: cuttingTechnologies } = useGetCuttingTechnologiesQuery({ organisationId })
    const { data: rateTables } = useGetRateTablesQuery({ organisationId })
    const { data: rates } = useGetRatesQuery({ organisationId })

    const { n } = useNumberFormatter()

    const customersMaterialOptions = [
        { label: t('Yes'), value: 'true' },
        { label: t('No'), value: 'false' },
    ]
    const consumptionModeOptions = [
        {
            label: t(mode === 'flat' ? 'Whole Sheet' : 'Whole Length'),
            value: 'WholeSheet',
        },
        { label: t('Nest Bounds'), value: 'NestBounds' },
    ]

    const grainDirectionOptions = [
        { label: t('None'), value: 'None' },
        { label: t('Horizontal'), value: 'Horizontal' },
        { label: t('Vertical'), value: 'Vertical' },
    ]

    // Validate rate table thickness
    const sheetHasRateTableThickness = useCallback(
        (sheet, rateTable) => {
            if (!sheet || !rateTable || !rates) return false

            const sortedRates = Object.values(rates)
                .filter((rate) => rate.rateTableId === rateTable.rateTableId && !rate.isDeleted)
                .sort((a, b) => a.thickness - b.thickness)

            return (
                sortedRates.length > 0 &&
                sheet.thickness >= sortedRates[0].thickness &&
                sheet.thickness <= sortedRates[sortedRates.length - 1].thickness
            )
        },
        [rates]
    )

    const filterSheet = useCallback(
        (sheet) => {
            const cuttingTech = sheet?.sheetCuttingTechnology?.find(
                (cuttingTech) => cuttingTech?.cuttingTechnologyId === cuttingTechnologyValue
            )

            const isNotDisabled = cuttingTech?.isEnabled ?? true

            return !sheet?.isDeleted && isNotDisabled
        },
        [cuttingTechnologyValue]
    )

    const getSheetsByMaterialId = useCallback(
        (materialId, applyFilter = true) => {
            if (!materials || !sheets || !materialId) return []

            const material = materials[materialId]
            if (!material?.sheetIds) return []

            let mappedSheets = material.sheetIds.map((sheetId) => sheets[sheetId]).filter(Boolean) // Remove undefined/null values

            // Apply filterSheet conditionally
            if (applyFilter) {
                mappedSheets = mappedSheets.filter(filterSheet)
            }

            return mappedSheets
        },
        [materials, sheets, filterSheet]
    )

    const getMaterialRateTable = useCallback(
        (material) => {
            if (!material?.materialRateTables?.length) return null

            return material.materialRateTables.find(
                (rateTable) => rateTables?.[rateTable.rateTableId]?.cuttingTechnologyId === cuttingTechnologyValue
            )
        },
        [rateTables, cuttingTechnologyValue]
    )

    const matchesProfileDimensions = useCallback(
        (sheet) => {
            if (!profileValue || !profileDimensionsValue) return true
            if (sheet?.profile !== profileValue) return false

            // Match dimensions based on profile type
            if (profileValue === 'Circle') {
                return n(sheet.diameter) === profileDimensionsValue
            }
            if (profileValue === 'Square') {
                return n(sheet.sheetWidth) === profileDimensionsValue
            }
            if (profileValue === 'FlatSidedOval') {
                const sheetDimensions = n(sheet?.sheetWidth) + ' x ' + n(sheet?.sheetHeight)
                return sheetDimensions === profileDimensionsValue
            }

            // Default case for other profile types
            const sheetDimensions = n(sheet?.sheetWidth) + ' x ' + n(sheet?.sheetHeight)
            const partDimensions = profileDimensionsValue.split(' x ')
            const partDimensionsReversed = partDimensions.reverse().join(' x ')

            return sheetDimensions === profileDimensionsValue || sheetDimensions === partDimensionsReversed
        },
        [profileValue, profileDimensionsValue, n]
    )

    // Helper function to format sheet size consistently
    const formatSheetSize = useCallback(
        (sheet) => {
            if (!sheet) return ''
            if (sheet.profile === 'Circle') return n(sheet.diameter)
            if (sheet.profile === 'Square') return n(sheet.sheetWidth)
            return n(sheet.sheetWidth) + ' x ' + n(sheet.sheetHeight)
        },
        [n]
    )

    const cuttingTechOptions = useMemo(() => {
        if (!cuttingTechnologies) return []

        return Object.values(cuttingTechnologies).filter(
            (tech) => tech?.type?.toLowerCase() === mode && !tech?.isDeleted
        )
    }, [cuttingTechnologies, mode])

    const materialRateTableFilter = useCallback(
        (materialRateTable) =>
            rateTables && rateTables[materialRateTable.rateTableId]?.cuttingTechnologyId === cuttingTechnologyValue,
        [rateTables, cuttingTechnologyValue]
    )

    const materialFilter = useCallback(
        (material) =>
            material.materialRateTables.some(materialRateTableFilter) &&
            !material.isDeleted &&
            material.type.toLowerCase() === mode,
        [materialRateTableFilter, mode]
    )

    const materialOptions = useMemo(() => {
        if (!materials) return []

        return Object.values(materials).filter(materialFilter)?.sort(compareMaterials)
    }, [materials, materialFilter])

    const profileOptions = useMemo(() => {
        if (mode === 'flat' || !showTubeOptions) return []
        if (!materialValue) return []

        const sheetsForMaterial = getSheetsByMaterialId(materialValue)

        const uniqueProfiles = new Set()
        sheetsForMaterial?.forEach((sheet) => {
            if (sheet?.profile) uniqueProfiles.add(sheet.profile)
        })
        return Array.from(uniqueProfiles).sort()
    }, [materialValue, mode, showTubeOptions, getSheetsByMaterialId])

    const profileDimensions = useMemo(() => {
        if (mode === 'flat' || !showTubeOptions) return []
        if (!materialValue || !profileValue) return []

        const sheetsForMaterial = getSheetsByMaterialId(materialValue)?.filter(
            (sheet) => sheet?.profile === profileValue
        )

        const profileDimensions = sheetsForMaterial?.map(formatSheetSize).sort((a, b) => {
            if (a < b) return -1
            if (a > b) return 1
            return 0
        })
        return [...new Set(profileDimensions ?? [])]
    }, [materialValue, mode, showTubeOptions, profileValue, getSheetsByMaterialId, filterSheet, formatSheetSize])

    const thicknessOptions = useMemo(() => {
        if (!materials || !sheets || !materialValue) return []

        const material = materials[materialValue]
        if (!material) return []

        const materialRateTable = getMaterialRateTable(material)

        const rateTableToCheck = rateTables?.[materialRateTable?.rateTableId]

        const uniqueThicknesses = new Set()

        getSheetsByMaterialId(materialValue)
            ?.filter((sheet) => sheetHasRateTableThickness(sheet, rateTableToCheck))
            .filter((sheet) => {
                if (mode !== 'flat' && profileValue && profileDimensionsValue) {
                    return matchesProfileDimensions(sheet)
                }
                return true
            })
            .forEach((sheet) => {
                if (sheet?.thickness !== undefined) uniqueThicknesses.add(sheet.thickness)
            })

        return Array.from(uniqueThicknesses).sort((a, b) => a - b)
    }, [
        materialValue,
        materials,
        getSheetsByMaterialId,
        filterSheet,
        sheetHasRateTableThickness,
        rateTables,
        mode,
        profileValue,
        profileDimensionsValue,
        matchesProfileDimensions,
        getMaterialRateTable,
    ])

    const sheetOptions = useMemo(() => {
        if (!materials || !sheets || !materialValue || !thicknessValue) return []

        const material = materials[materialValue]
        if (!material) return []

        let sheetsForMaterial = getSheetsByMaterialId(materialValue)?.filter(
            (sheet) => sheet?.thickness === thicknessValue
        )

        if (mode !== 'flat') {
            sheetsForMaterial = sheetsForMaterial.filter((sheet) =>
                isThicknessWithinTolerance(sheet, thicknessValue, partDimensions, organisation)
            )

            // Apply profile dimension filtering
            if (profileValue && profileDimensionsValue) {
                sheetsForMaterial = sheetsForMaterial.filter(matchesProfileDimensions)
            }
        }

        if (!sheetsForMaterial?.length) return []

        return sheetsForMaterial.map((sheet) => {
            const sheetSize = formatSheetSize(sheet)
            if (sheet.sheetSize === sheetSize) return sheet

            return { ...sheet, sheetSize }
        })
    }, [
        materialValue,
        thicknessValue,
        materials,
        sheets,
        filterSheet,
        formatSheetSize,
        mode,
        partDimensions,
        organisation,
        profileValue,
        profileDimensionsValue,
        matchesProfileDimensions,
    ])

    return {
        cuttingTechOptions,
        materialOptions,
        thicknessOptions,
        sheetOptions,
        customersMaterialOptions,
        consumptionModeOptions,
        grainDirectionOptions,
        profileOptions,
        profileDimensions,
    }
}
